import dayjs from 'dayjs';
import type { E164Number } from 'libphonenumber-js';
import { API_BASE_URL, APP_PROXY_URL } from '../utils/constants';
import type { AddonProduct } from './Addons/useAddons';
import type { EventType } from '../../lib/events';
import { captureMessage } from '@sentry/react';

export class UnauthorizedError extends Error {
  res;

  constructor(res) {
    super('UnauthorizedError');
    this.res = res;
  }
}

// Used exclusively to access API from customer portal
export const fetchApi = async ({
  url,
  method = 'GET',
  query = {},
  body = undefined,
  headers = {},
  authToken = undefined,
}: {
  url: string;
  method?: string;
  query?: any;
  body?: any;
  headers?: any;
  authToken?: any;
}) => {
  if (authToken) {
    headers['authorization'] = authToken;
  }

  if (method === 'POST' || method === 'PUT') {
    headers['content-type'] = 'application/json';
  }

  if (body) {
    body = JSON.stringify(body);
  }

  const searchParams = new URLSearchParams(query);

  const fetchUrl = `${API_BASE_URL}${url}?${searchParams}`;

  const res = await fetch(fetchUrl, {
    method,
    body,
    headers,
  });

  // General error handling
  if (!res.ok && res.status === 401) {
    throw new UnauthorizedError(res);
  }

  return res;
};

export const getSubscriptions = async (
  customerId?: string,
  authToken?: string,
  expandLines = false,
): Promise<Subscriptions> => {
  const res = await fetchApi({
    url: `/customers/${customerId}/subscriptions`,
    query: { expandLines },
    authToken,
  });

  if (!res.ok) {
    throw new Error('There was an error getting subscriptions');
  }
  const data = await res.json();
  return data;
};

export const getSubscription = async (
  customerId?: string,
  subscriptionId?: string,
  authToken?: string,
): Promise<Subscription> => {
  const res = await fetchApi({
    url: `/customers/${customerId}/subscriptions/${subscriptionId}`,
    authToken,
    query: { getInventory: true },
  });
  if (!res.ok) {
    throw new Error('There was an error getting a subscription');
  }
  const data = await res.json();
  return data;
};

export const getProductVariantsImages = async (
  customerId?: string,
  subscriptionId?: string,
  authToken?: string,
  productVariantsIds: string[] = [],
): Promise<
  {
    gId: string;
    title: string;
    image: { url: string };
    product: { gId: string; title: string; featuredImage: { url: string } };
  }[]
> => {
  const res = await fetchApi({
    url: `/customers/${customerId}/subscriptions/${subscriptionId}/productVariants/images`,
    method: 'POST',
    authToken,
    body: { productVariantsIds },
  });
  if (!res.ok) {
    throw new Error('There was an error getting images');
  }
  const data = await res.json();
  return data;
};

export const getSubscriptionOriginOrder = async (
  customerId?: string,
  subscriptionId?: string,
  authToken?: string,
): Promise<OriginOrder> => {
  const res = await fetchApi({
    url: `/customers/${customerId}/subscriptions/${subscriptionId}/origin-order`,
    authToken,
  });
  if (!res.ok) {
    throw new Error('There was an error getting the subscription origin order');
  }
  const data = await res.json();
  return data;
};

export const forceUpdateSubscription = async (
  customerId?: string,
  subscriptionId?: string,
  authToken?: string,
): Promise<Response> => {
  const res = await fetchApi({
    url: `/customers/${customerId}/subscriptions/${subscriptionId}/force-update`,
    method: 'POST',
    authToken,
  });

  return res;
};

export const getSubscriptionOriginOrderFulfillments = async (
  customerId?: string,
  subscriptionId?: string,
  authToken?: string,
) => {
  const res = await fetchApi({
    url: `/customers/${customerId}/subscriptions/${subscriptionId}/origin-order/fulfillments`,
    authToken,
  });
  if (!res.ok) {
    throw new Error('There was an error getting origin order fulfillments');
  }
  const data = await res.json();
  return data;
};

export const rescheduleOrderFulfillment = async (
  fulfillmentId: string,
  fulfillAt: string,
  customerId?: string,
  subscriptionId?: string,
  authToken?: string,
) => {
  const res = await fetchApi({
    url: `/customers/${customerId}/subscriptions/${subscriptionId}/origin-order/fulfillments/${fulfillmentId}`,
    method: 'POST',
    body: { fulfillAt },
    authToken,
  });
  if (!res.ok) {
    throw new Error('There was an error getting a subscription fulfillments');
  }
  const data = await res.json();
  return data;
};

export type LineRequest = {
  id: string | null;
  isSoldOut?: boolean;
  input: Record<string, unknown>;
};

export type subscriptionLineOperation = 'update' | 'add' | 'remove';
export interface LineResponse {
  id: string;
  variantId: string;
  title: string;
  variantTitle: string;
  quantity: number;
  productId: string;
  updatedPrices: ComputedPricingPolicy;
  operation: subscriptionLineOperation;
  customAttributes: Record<string, unknown>[];
  available?: boolean;
  BaBOutOfStockVariants?: string[];
}

export const updateBabProductForBundle = async ({
  customerId,
  subscriptionId,
  authToken,
  productVariantId,
  lineId,
  selectedItems,
  bundleProps,
  sellingPlan,
  oneTimeAddon,
  discountCodes,
}: {
  customerId?: string;
  subscriptionId?: string;
  authToken?: string;
  productVariantId?: string;
  lineId?: string;
  selectedItems?: string[];
  bundleProps?: Record<string, unknown>;
  discountCodes?: string[];
  sellingPlan?: SellingPlan;
  oneTimeAddon?: boolean;
}): Promise<any> => {
  const res = await fetchApi({
    url: `/customers/${customerId}/subscriptions/${subscriptionId}/swap-product-for-bab`,
    method: 'POST',
    body: {
      productVariantId,
      selectedItems,
      bundleProps,
      lineId,
      sellingPlan,
      oneTimeAddon,
      discountCodes,
      isAddon: oneTimeAddon,
    },
    authToken,
  });
  if (!res.ok) {
    throw new Error('There was an error updating a subscription line');
  }
  const data = await res.json();
  return data;
};

export const addBabProductForSubscription = async ({
  customerId,
  subscriptionId,
  authToken,
  productVariantId,
  selectedItems,
  bundleProps,
  sellingPlan,
  discountCodes,
  oneTimeAddon,
  addonGroupId,
  forceSyncUpdate,
}: {
  customerId?: string;
  subscriptionId?: string;
  authToken?: string;
  productVariantId?: string;
  selectedItems?: string[];
  bundleProps?: Record<string, unknown>;
  sellingPlan?: string | number;
  discountCodes?: string[];
  oneTimeAddon?: boolean;
  addonGroupId?: string;
  forceSyncUpdate?: boolean;
}): Promise<any> => {
  const res = await fetchApi({
    url: `/customers/${customerId}/subscriptions/${subscriptionId}/add-bab`,
    method: 'POST',
    body: {
      productVariantId,
      selectedItems,
      bundleProps,
      sellingPlan,
      discountCodes,
      oneTimeAddon,
      isAddon: true,
      addonGroupId,
      forceSyncUpdate,
    },
    authToken,
  });

  if (!res.ok) {
    throw new Error('There was an error adding a product to a subscription');
  }
  const data = await res.json();
  return data;
};

export const changeSCL = async (
  lines: LineRequest[],
  customerId?: string,
  subscriptionId?: string,
  authToken?: string,
  sellingPlan?: string | number,
  discountCodes?: string[],
  forceSyncUpdate = false,
): Promise<{
  subscriptionContractId: string;
  updatedPrices: ComputedPricingPolicy[];
  lines: LineResponse[];
  subscriptionContract: Subscription;
}> => {
  const res = await fetchApi({
    url: `/customers/${customerId}/subscriptions/${subscriptionId}/lines`,
    method: 'POST',
    body: { lines, sellingPlan, discountCodes, forceSyncUpdate },
    authToken,
  });
  if (!res.ok) {
    throw new Error('There was an error updating subscription lines');
  }
  const data = await res.json();
  return data;
};

export const cancelSubscription = async (
  customerId?: string,
  subscriptionId?: string,
  authToken?: string,
  reason?: string,
  details?: string,
): Promise<Subscription> => {
  const res = await fetchApi({
    url: `/customers/${customerId}/subscriptions/${subscriptionId}/cancel`,
    method: 'POST',
    body: {
      reason,
      details,
    },
    authToken,
  });
  if (!res.ok) {
    throw new Error('There was an error canceling a subscription');
  }
  const data = await res.json();
  return data;
};

export const disableAutorenew = async (
  customerId?: string,
  subscriptionId?: string,
  authToken?: string,
  reason?: string,
  details?: string,
): Promise<Subscription> => {
  const res = await fetchApi({
    url: `/customers/${customerId}/subscriptions/${subscriptionId}/disable-autorenew`,
    method: 'POST',
    body: {
      ...(reason && { reason }),
      ...(details && { details }),
    },
    authToken,
  });
  if (!res.ok) {
    throw new Error('There was an error disabling autorenew');
  }
  const data = await res.json();
  return data;
};

export const requestPaymentChange = async (
  customerId?: string,
  subscriptionId?: string,
  authToken?: string,
): Promise<string> => {
  const res = await fetchApi({
    url: `/customers/${customerId}/subscriptions/${subscriptionId}/send-customer-payment-email`,
    method: 'POST',
    body: {},
    authToken,
  });

  if (!res.ok) {
    throw new Error('There was an error requesting a payment change');
  }

  const data = await res.text();
  return data;
};

export const getSubscriptionBillingAttempts = async (
  customerId?: string,
  subscriptionId?: string,
  authToken?: string,
): Promise<BillingAttempts> => {
  const res = await fetchApi({
    url: `/customers/${customerId}/subscriptions/${subscriptionId}/billing-attempts`,
    query: { limit: 300 },
    authToken,
  });

  if (!res.ok) {
    throw new Error(
      'There was an error getting a subscription billing attempts',
    );
  }
  const data = await res.json();
  return data;
};

export const getSubscriptionBillingAttemptRetries = async (
  customerId?: string,
  subscriptionId?: string,
  authToken?: string,
): Promise<SubscriptionBillingAttemptRetry[]> => {
  const res = await fetchApi({
    url: `/customers/${customerId}/subscriptions/${subscriptionId}/billing-attempt-retries`,
    query: { limit: 300 },
    authToken,
  });

  if (!res.ok) {
    throw new Error(
      'There was an error getting a subscription billing attempt retries',
    );
  }
  const data = await res.json();
  return data;
};

export const getShopConfiguration = async (
  customerId?: string,
  shopDomain?: string,
  authToken?: string,
): Promise<ShopConfiguration> => {
  const res = await fetchApi({
    url: `/shops/configuration/${customerId}`,
    method: 'POST',
    body: { shopDomain },
    authToken,
  });

  const data = await res.json();

  if (!res.ok) {
    throw new Error('There was an error getting the shop configuration');
  }

  return data;
};

export const getSCAvailableSellingPlans = async (
  sellingPlanIds: string[],
  customerId?: string,
  subscriptionContractId?: string,
  authToken?: string,
): Promise<SellingPlan[]> => {
  const res = await fetchApi({
    url: `/customers/${customerId}/subscriptions/${subscriptionContractId}/available-selling-plans`,
    authToken,
    query: {
      sellingPlanIds: sellingPlanIds.join(','),
    },
  });

  const data = await res.json();

  if (!res.ok) {
    throw new Error(
      'There was an error getting the SC available selling plans',
    );
  }

  return data;
};

export const nextBillingDate = async (
  newBillingDate: dayjs.Dayjs,
  customerId?: string,
  subscriptionId?: string,
  authToken?: string,
  eventType?: EventType,
): Promise<Subscription> => {
  const payloadObj = {
    nextBillingDate: newBillingDate.format('YYYY-MM-DD'),

    eventType,
  };
  const res = await fetchApi({
    url: `/customers/${customerId}/subscriptions/${subscriptionId}/next-billing-date`,
    method: 'POST',
    body: payloadObj,
    authToken,
  });
  const data = await res.json();
  if (res.status >= 400) {
    throw new Error('There was an error setting the next billing date');
  }
  return data;
};

export const reactivateSc = async (
  customerId?: string,
  subscriptionId?: string,
  authToken?: string,
  nextBillingDate?: string,
): Promise<Subscription> => {
  const res = await fetchApi({
    url: `/customers/${customerId}/subscriptions/${subscriptionId}/reactivate`,
    method: 'POST',
    authToken,
    body: {
      nextBillingDate,
    },
  });
  const data = await res.json();
  if (res.status >= 400) {
    throw new Error('There was an error trying to reactivate the subscription');
  }
  return data;
};

export const skipSc = async (
  customerId?: string,
  subscriptionId?: string,
  authToken?: string,
  intervalId?: string,
): Promise<{ subscriptionContract: Subscription; nextBillingDate: string }> => {
  const res = await fetchApi({
    url: `/customers/${customerId}/subscriptions/${subscriptionId}/skip`,
    method: 'POST',
    authToken,
    ...(intervalId ? { body: { intervalId } } : {}),
  });
  const data = await res.json();
  if (res.status >= 400) {
    throw new Error('There was an error trying to skip the subscription');
  }
  return data;
};

export const orderNow = async (
  customerId?: string,
  subscriptionId?: string,
  manualRetry?: boolean,
  authToken?: string,
): Promise<Subscription> => {
  const res = await fetchApi({
    url: `/customers/${customerId}/subscriptions/${subscriptionId}/order-now`,
    method: 'POST',
    body: { manualRetry },
    authToken,
  });
  const data = await res.json();
  if (res.status >= 400) {
    throw new Error('There was an error setting ordering now');
  }
  return data;
};

export const pauseSubscription = async (
  customerId?: string,
  subscriptionId?: string,
  authToken?: string,
): Promise<Subscription> => {
  const res = await fetchApi({
    url: `/customers/${customerId}/subscriptions/${subscriptionId}/pause`,
    method: 'POST',
    body: {},
    authToken,
  });
  if (!res.ok) {
    throw new Error('There was an error pausing the subscription');
  }
  const data = await res.json();
  return data;
};

export const addDiscountCode = async (
  discountCode: string,
  customerId?: string,
  subscriptionId?: string,
  authToken?: string,
): Promise<Subscription> => {
  const res = await fetchApi({
    url: `/customers/${customerId}/subscriptions/${subscriptionId}/discount-codes/${discountCode}`,
    method: 'POST',
    body: {},
    authToken,
  });
  if (!res.ok) {
    throw new Error('There was an error adding a discount code');
  }
  const data = await res.json();
  return data;
};

export const addDiscount = async (
  customerId?: string,
  subscriptionId?: string,
  authToken?: string,
): Promise<Subscription> => {
  const res = await fetchApi({
    url: `/customers/${customerId}/subscriptions/${subscriptionId}/discount`,
    method: 'POST',
    body: {},
    authToken,
  });
  if (!res.ok) {
    throw new Error('There was an error adding a discount');
  }
  const data = await res.json();
  return data;
};

export const removeDiscountCode = async (
  discountId: string,
  discountCode: string,
  customerId?: string,
  subscriptionId?: string,
  authToken?: string,
): Promise<Subscription> => {
  const res = await fetchApi({
    url: `/customers/${customerId}/subscriptions/${subscriptionId}/delete-discounts/`,
    method: 'POST',
    body: {
      discountId,

      discountCode,
    },
    authToken,
  });
  if (!res.ok) {
    throw new Error('There was an error removing the discount code');
  }
  const data = await res.json();
  return data;
};

export const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

export const getShippingOptions = async (
  customerId?: string,
  subscriptionId?: string,
  authToken?: string,
  body?: {
    deliveryAddress: ModalFormAddress;
  },
): Promise<ShippingOptionsResponse> => {
  const res = await fetchApi({
    url: `/customers/${customerId}/subscriptions/${subscriptionId}/shipping-options?includeLocalDelivery=true`,
    method: 'POST',
    body,
    authToken,
  });
  const data = await res.json();

  if (data.shippingOptions === null) {
    await sleep(200);
    return getShippingOptions(customerId, subscriptionId, authToken, body);
  } else {
    return data;
  }
};

export const saveSubShippingMethod = async (
  customerId?: string,
  subscriptionId?: string,
  authToken?: string,
  body?: {
    draftId: string;
    shippingOptionCode: string;
    deliveryAddress: ModalFormAddress;
    localDeliveryOptions?: {
      phone: string | undefined;
      instructions: string | undefined;
    };
  },
): Promise<SaveShippingMethodResponse> => {
  const res = await fetchApi({
    url: `/customers/${customerId}/subscriptions/${subscriptionId}/delivery-method`,
    method: 'POST',
    body,
    authToken,
  });
  if (!res.ok) {
    const { userErrors, message } = (await res.json()) || {};

    const errorMessage =
      userErrors?.[0]?.message ||
      message ||
      'There was an error saving a shipping method';
    throw new Error(errorMessage);
  }
  const data = await res.json();
  return data;
};

export const updateShopifyAddress = async ({
  addressId,
  address,
  customerId,
  authToken,
  shopDomain,
}: {
  addressId: string;
  address: ModalFormAddress;
  customerId?: string;
  authToken?: string;
  shopDomain?: string;
}): Promise<Response> => {
  const res = await fetchApi({
    url: `/customers/${customerId}/address/${addressId}`,
    method: 'PUT',
    authToken,
    body: { address, shopDomain },
  });

  if (!res.ok) {
    throw new Error('There was an error updating the address');
  }
  return res;
};

export const createShopifyAddress = async ({
  address,
  customerId,
  authToken,
  shopDomain,
}: {
  address: ModalFormAddress;
  customerId?: string;
  authToken?: string;
  shopDomain?: string;
}): Promise<Response> => {
  const res = await fetchApi({
    url: `/customers/${customerId}/address/`,
    method: 'POST',
    authToken,
    body: { address, shopDomain },
  });

  if (!res.ok) {
    throw new Error('There was an error creating the address');
  }
  return res;
};

export const updateFrequency = async (
  customerId?: string,
  subscriptionId?: string,
  authToken?: string,
  body?: {
    interval: string;
    intervalCount: number;
    isCustom: boolean;
  },
): Promise<SubscriptionFrequency> => {
  const res = await fetchApi({
    url: `/customers/${customerId}/subscriptions/${subscriptionId}/frequency`,
    method: 'POST',
    body,
    authToken,
  });

  const data = await res.json();
  if (!res.ok) {
    throw new Error('There was an error updating a subscription frequency');
  }
  return data;
};

export const updateCustomer = async (
  customerId?: string,
  authToken?: string,
  subscriptionId?: string,
  body?: {
    phone: E164Number | string;
    acceptsSMSMarketing: boolean;
  },
): Promise<Customer> => {
  const res = await fetchApi({
    url: `/customers/${customerId}/subscriptions/${subscriptionId}/update-customer`,
    method: 'PUT',
    body,
    authToken,
  });

  const data = await res.json();
  if (!res.ok) {
    throw new Error('There was an error updating a customer information');
  }
  return data;
};

interface IAllProductsApi {
  customerId?: string;
  authToken?: string;
  apiAuhToken?: string;
  shopDomain?: string;
  lineSellingPlanGid?: string;
  page?: number;
  itemsPerPage?: number;
}
export interface ProductsResponse {
  sellingPlanGroup: {
    productVariants: {
      edges: { node: APIVariant }[];
    };
    products: {
      edges: { node: APIVariantProduct }[];
    };
  };
}

export interface ProductsDataResponse {
  productVariants: APIDataVariant[];
  currencyCode?: string;
  meta: {
    page: number;
    hasNextPage: boolean;
  };
}

export const getAllProducts = async ({
  customerId,
  authToken,
  shopDomain,
  lineSellingPlanGid,
  page = 1,
  itemsPerPage = 50,
}: IAllProductsApi): Promise<ProductsResponse[] | ProductsDataResponse> => {
  if (!authToken || !customerId)
    throw new Error('authToken and customerId are required');
  if (!shopDomain) throw new Error('ShopDomain is required');

  const res = await fetchApi({
    url: `/shops/products/${customerId}`,
    method: 'POST',
    query: {
      page,
      itemsPerPage,
    },
    body: { shopDomain, lineSellingPlanGid },
    authToken,
  });

  if (!res.ok) {
    throw new Error('Error getting all products');
  }

  const json = await res.json();
  if (!json) throw new Error('Error getting all products');

  return json;
};

export const getBundleByProductId = async ({
  productId,
  authToken,
}: {
  productId: string;
  authToken?: string;
}): Promise<LiquidBundle> => {
  if (!authToken) throw new Error('authToken is required');
  const url = `${APP_PROXY_URL}/api/bundle/${productId}`;
  const res = await fetch(url, {
    headers: { 'Content-Type': 'text/plain' },
  });
  if (!res.ok) {
    throw new Error('Error getting bundle');
  }
  const response = await res.text();
  if (!response) throw new Error('Error getting bundle');

  return JSON.parse(response) as LiquidBundle;
};

export const getShuffleNextProducts = async (
  subscriptionId?: string,
  lineId?: string,
  authToken?: string,
): Promise<{
  upcomingProducts: ShuffleNextProduct[];
  skipAllowed: boolean;
}> => {
  const res = await fetchApi({
    url: `/subscriptions/${subscriptionId}/lines/${lineId}/upcoming-products`,
    method: 'GET',
    authToken,
  });
  const data = await res.json();
  if (res.status >= 400) {
    captureMessage('Error getting upcoming products');
    return { upcomingProducts: [], skipAllowed: false };
  }
  return data;
};

export const skipShuffleProduct = async (
  subscriptionId?: string,
  lineId?: string,
  productGId?: string,
  authToken?: string,
): Promise<{
  upcomingProducts: ShuffleNextProduct[];
  previousProducts: ShuffleNextProduct[];
  remainingProducts: ShuffleNextProduct[];
  skipAllowed: boolean;
}> => {
  const res = await fetchApi({
    url: `/subscriptions/${subscriptionId}/lines/${lineId}/skip-shuffle-product`,
    method: 'PUT',
    authToken,
    body: { productGId },
  });
  const data = await res.json();
  if (res.status >= 400) {
    captureMessage('Error skipping product');
    return {
      upcomingProducts: [],
      previousProducts: [],
      remainingProducts: [],
      skipAllowed: false,
    };
  }
  return data;
};

export const unskipShuffleProduct = async (
  subscriptionId?: string,
  lineId?: string,
  productGId?: string,
  authToken?: string,
): Promise<{
  upcomingProducts: ShuffleNextProduct[];
  previousProducts: ShuffleNextProduct[];
  remainingProducts: ShuffleNextProduct[];
  skipAllowed: boolean;
}> => {
  const res = await fetchApi({
    url: `/subscriptions/${subscriptionId}/lines/${lineId}/unskip-shuffle-product`,
    method: 'PUT',
    authToken,
    body: { productGId },
  });
  const data = await res.json();
  if (res.status >= 400) {
    captureMessage('Error unskipping product');
    return {
      upcomingProducts: [],
      previousProducts: [],
      remainingProducts: [],
      skipAllowed: false,
    };
  }
  return data;
};

export const getAddons = async (
  subscriptionId: string,
  { customerId, authToken }: { customerId: string; authToken?: string },
): Promise<AddonProduct[]> => {
  const addonsEror = 'Error retrieving addons';
  try {
    const res = await fetchApi({
      url: `/customers/${customerId}/subscriptions/${subscriptionId}/addons`,
      authToken,
    });
    if (!res.ok) {
      throw new Error(addonsEror);
    }
    const json = await res.json();
    return json.products || json.addons; // ADDONS_V2 JSON RESPONSE. Remove `json.products` when update addons
  } catch (e) {
    throw new Error(addonsEror);
  }
};

export const getProductById = async (
  {
    customerId,
    authToken,
    subscriptionId,
  }: {
    customerId: string | undefined;
    authToken: string | undefined;
    subscriptionId: string | undefined;
  },
  productId: string,
): Promise<APIProduct> => {
  if (!customerId) throw new Error('authToken and customerId are required');
  if (!subscriptionId) throw new Error('subscriptionId is required');

  const res = await fetchApi({
    url: `/customers/${customerId}/subscriptions/${subscriptionId}/products/${productId}`,
    authToken,
  });

  if (!res.ok) {
    throw new Error('Error fetching a product');
  }

  const json = await res.json();
  return json;
};
export const getProductsByHandle = async ({
  customerId,

  subscriptionId,
  productsHandles = [],
}: {
  customerId: string | undefined;
  authToken: string | undefined;
  subscriptionId: string | undefined;
  productsHandles: string[];
}): Promise<{
  products: { id: string; productData: LiquidProduct }[];
  countryCode: string;
  currencyCode: string;
}> => {
  if (!customerId) throw new Error('authToken and customerId are required');
  if (!subscriptionId) throw new Error('subscriptionId is required');
  const url = `${APP_PROXY_URL}/api/products?productsHandles=${productsHandles.join(
    ',',
  )}`;

  const res = await fetch(url);

  if (!res.ok) {
    throw new Error('Error getting products');
  }
  const response = await res.json();

  return response;
};

export const addNewPaymentMehod = async ({
  customerId,
  authToken,
  subscriptionId,
  data,
  subscriptionIsInDunning,
}: {
  customerId: string | undefined;
  authToken: string | undefined;
  subscriptionId: string | undefined;
  data: FormNewPaymentMethod | undefined;
  subscriptionIsInDunning: boolean;
}): Promise<{ customerPaymentMethod: CustomerPaymentMethod }> => {
  if (!authToken || !customerId)
    throw new Error('authToken and customerId are required');
  if (!subscriptionId) throw new Error('subscriptionId is required');
  if (!data) throw new Error('Data is required');
  const billingAddress = {
    address1: data.address1,
    address2: data.address2,
    city: data.city,
    country: data.country,
    firstName: data.firstName,
    lastName: data.lastName,
    phone: data.phone,
    province: data.province,
    zip: data.zip,
  };
  const [month, year] = data.expDate.split('/');
  const credit_card = {
    first_name: data.cardFirstName,
    last_name: data.cardLastName,
    month,
    number: data.cardNumber,
    verification_value: data.securityCode,
    year,
  };
  const res = await fetchApi({
    url: `/customers/${customerId}/subscriptions/${subscriptionId}/payment-method`,

    method: 'POST',

    body: {
      billingAddress,
      credit_card,
      subscriptionIsInDunning,
    },
    authToken,
  });

  const json = await res.json();

  if (!res.ok) {
    throw new Error(json.error || 'Error adding payment method');
  }

  return json;
};

export const getProductsImages = async ({
  customerId,
  authToken,
  subscriptionId,
  products = [],
}: {
  customerId: string | undefined;
  subscriptionId: string;
  authToken: string | undefined;
  products: string[] | undefined;
}) => {
  const res = await fetchApi({
    url: `/customers/${customerId}/subscriptions/${subscriptionId}/products/images`,
    method: 'POST',
    body: {
      productIds: products,
    },
    authToken,
  });

  if (!res.ok) {
    throw new Error('Error getting products and variants images');
  }

  try {
    const json = await res.json();
    return json;
  } catch (e) {
    console.error(e);
    throw new Error('Error getting products and variants images');
  }
};

export const getShopSellingPlans = async ({
  customerId,
  authToken,
  shopDomain,
}: {
  customerId: string | undefined;
  authToken: string | undefined;
  shopDomain: string | undefined;
}) => {
  const res = await fetchApi({
    url: `/shops/selling-plans/${customerId}`,
    method: 'POST',
    body: { shopDomain },
    authToken,
  });

  if (!res.ok) {
    throw new Error('Error getting shop selling plans');
  }

  try {
    const json = await res.json();
    return json;
  } catch (e) {
    console.error(e);
    throw new Error('Error getting shop selling plans');
  }
};

export const loginStart = async ({
  email,
  shopDomain,
}: {
  email: string;
  shopDomain: string;
}): Promise<{ success: boolean; data: any }> => {
  const res = await fetchApi({
    url: `/customers/login/start`,
    method: 'POST',
    body: { email, shopDomain },
  });

  let data;
  try {
    data = await res.json();
  } catch {
    data = null;
  }

  return {
    success: res.ok,
    data,
  };
};

export const loginAuth = async ({
  customerId,
  code,
  shopDomain,
}: {
  customerId: string;
  code: string;
  shopDomain: string;
}): Promise<{ success: boolean; data: any }> => {
  const res = await fetchApi({
    url: `/customers/login/auth`,
    method: 'POST',
    body: { customerId, code, shopDomain },
  });

  let data;
  try {
    data = await res.json();
  } catch {
    data = null;
  }

  return {
    success: res.ok,
    data,
  };
};

export const getCustomerAddress = async ({
  customerId,
  authToken,
}: {
  customerId: string;
  authToken?: string;
}): Promise<CustomerAddressApiResponse> => {
  try {
    const res = await fetchApi({
      url: `/customers/${customerId}/address`,
      authToken,
    });
    if (!res.ok) {
      throw new Error('Errror fetching customer address');
    }
    const json = await res.json();
    return json;
  } catch (e) {
    throw new Error('Errror fetching customer address');
  }
};
